import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { AUDIT_ACTIONS, useAuditEntityTypes, useAuditLog, type AuditEntry } from '../../api/audit';
import { Button, ButtonGroup, DateInput, EMPTY_DATE, ErrorSummary, Input, Pagination, Select, Table, isoToParts, partsToIso, type DateParts } from '../../components/idsk';
import { PageHeading } from '../../layout/PageHeading';
import { usePageTitle } from '../../layout/usePageTitle';
import { useAnnouncer } from '../../layout/Announcer';
import { useListParams } from '../../lib/useListParams';
import { formatDateTime } from '../../lib/format';
import { QueryError } from '../common/QueryError';
import { Loading } from '../common/LoadingPage';
import { RequireAdmin } from '../common/RequireAdmin';
import { ActorName, useAuditLabels } from './auditLabels';

const FILTERS = ['from', 'to', 'user', 'action', 'entityType', 'entityId'] as const;

export function AuditLogPage() {
  return (
    <RequireAdmin>
      <AuditLog />
    </RequireAdmin>
  );
}

function AuditLog() {
  const { t } = useTranslation();
  const labels = useAuditLabels();
  const { announce } = useAnnouncer();
  const list = useListParams(FILTERS, 'occurredAt,desc');
  const entityTypes = useAuditEntityTypes();
  const [draft, setDraft] = useState({ user: list.filters.user, action: list.filters.action, entityType: list.filters.entityType, entityId: list.filters.entityId });
  const [from, setFrom] = useState<DateParts>(isoToParts(list.filters.from));
  const [to, setTo] = useState<DateParts>(isoToParts(list.filters.to));
  const [dateErrors, setDateErrors] = useState<{ from?: string; to?: string }>({});
  const query = useAuditLog({
    from: list.filters.from || undefined,
    to: list.filters.to || undefined,
    user: list.filters.user || undefined,
    action: list.filters.action || undefined,
    entityType: list.filters.entityType || undefined,
    entityId: list.filters.entityId || undefined,
    page: list.page,
    size: 25,
    sort: list.sort,
  });
  usePageTitle([t('audit.title')]);

  const apply = (e: FormEvent) => {
    e.preventDefault();
    const f = partsToIso(from);
    const tt = partsToIso(to);
    const errors: { from?: string; to?: string } = {};
    if (f.kind === 'invalid') errors.from = t('audit.filters.invalidFrom');
    if (tt.kind === 'invalid') errors.to = t('audit.filters.invalidTo');
    if (!errors.from && !errors.to && f.kind === 'valid' && tt.kind === 'valid' && f.iso > tt.iso) errors.to = t('audit.filters.toBeforeFrom');
    setDateErrors(errors);
    if (errors.from || errors.to) return;
    list.update({ ...draft, from: f.kind === 'valid' ? f.iso : null, to: tt.kind === 'valid' ? tt.iso : null, page: null });
  };
  const clear = () => {
    setDraft({ user: '', action: '', entityType: '', entityId: '' });
    setFrom({ ...EMPTY_DATE });
    setTo({ ...EMPTY_DATE });
    setDateErrors({});
    list.update({ from: null, to: null, user: null, action: null, entityType: null, entityId: null, page: null });
  };
  const sortLabels: Record<string, string> = {
    occurredAt: t('audit.fields.occurredAt'),
    user: t('audit.fields.actor'),
    action: t('audit.fields.action'),
    entityType: t('audit.fields.entity'),
  };
  const errorItems = [
    ...(dateErrors.from ? [{ targetId: 'audit-from-day', text: dateErrors.from }] : []),
    ...(dateErrors.to ? [{ targetId: 'audit-to-day', text: dateErrors.to }] : []),
  ];

  return (
    <>
      <PageHeading>{t('audit.title')}</PageHeading>
      <p className="govuk-body">{t('audit.intro')}</p>
      {errorItems.length > 0 && <ErrorSummary errors={errorItems} />}
      <form className="app-filters" role="search" aria-label={t('audit.filters.label')} onSubmit={apply} noValidate>
        <h2 className="govuk-heading-s">{t('audit.filters.heading')}</h2>
        <div className="govuk-grid-row">
          <div className="govuk-grid-column-one-half">
            <DateInput id="audit-from" legend={t('audit.filters.from')} legendClassName="govuk-fieldset__legend--s" value={from} onChange={setFrom} error={dateErrors.from} />
          </div>
          <div className="govuk-grid-column-one-half">
            <DateInput id="audit-to" legend={t('audit.filters.to')} legendClassName="govuk-fieldset__legend--s" value={to} onChange={setTo} error={dateErrors.to} />
          </div>
        </div>
        <div className="govuk-grid-row">
          <div className="govuk-grid-column-one-half">
            <Input id="audit-user" label={t('audit.filters.user')} hint={t('audit.filters.userHint')} value={draft.user} onChange={(e) => setDraft({ ...draft, user: e.target.value })} />
          </div>
          <div className="govuk-grid-column-one-half">
            <Select
              id="audit-action"
              label={t('audit.fields.action')}
              placeholder={t('common.all')}
              options={AUDIT_ACTIONS.map((a) => ({ value: a, label: labels.action(a) }))}
              value={draft.action}
              onChange={(e) => setDraft({ ...draft, action: e.target.value })}
              width="full"
            />
          </div>
        </div>
        <div className="govuk-grid-row">
          <div className="govuk-grid-column-one-half">
            <Select
              id="audit-entity-type"
              label={t('audit.fields.entityType')}
              placeholder={t('common.all')}
              options={(entityTypes.data ?? []).map((e) => ({ value: e, label: labels.entityType(e) }))}
              value={draft.entityType}
              onChange={(e) => setDraft({ ...draft, entityType: e.target.value })}
              width="full"
            />
          </div>
          <div className="govuk-grid-column-one-half">
            <Input id="audit-entity-id" label={t('audit.fields.entityId')} hint={t('audit.filters.entityIdHint')} spellCheck={false} value={draft.entityId} onChange={(e) => setDraft({ ...draft, entityId: e.target.value })} />
          </div>
        </div>
        <ButtonGroup>
          <Button type="submit">{t('common.applyFilters')}</Button>
          <Button variant="secondary" onClick={clear}>
            {t('common.clearFilters')}
          </Button>
        </ButtonGroup>
      </form>
      {query.error ? (
        <QueryError error={query.error} />
      ) : query.isPending ? (
        <Loading />
      ) : (
        <>
          <Table<AuditEntry>
            caption={t('audit.tableCaption')}
            rows={query.data.content}
            rowKey={(e) => String(e.id)}
            sort={list.sortState}
            onSort={(key) => {
              const direction = list.toggleSort(key);
              announce(t('table.sortedBy', { column: sortLabels[key], direction: t(`table.${direction}`) }));
            }}
            columns={[
              {
                key: 'occurredAt',
                header: t('audit.fields.occurredAt'),
                sortable: true,
                rowHeader: true,
                render: (e) => (
                  <Link className="govuk-link" to={`/audit/${e.id}`}>
                    {formatDateTime(e.occurredAt)}
                    <span className="govuk-visually-hidden"> – {t('audit.detailLink')}</span>
                  </Link>
                ),
              },
              { key: 'user', header: t('audit.fields.actor'), sortable: true, render: (e) => <ActorName actor={e.actor} /> },
              { key: 'action', header: t('audit.fields.action'), sortable: true, render: (e) => labels.action(e.action) },
              { key: 'entityType', header: t('audit.fields.entity'), sortable: true, render: (e) => (e.entityType ? labels.entityType(e.entityType) : '') },
              { key: 'summary', header: t('audit.fields.summary'), render: (e) => e.summary },
            ]}
            emptyText={t('audit.empty')}
          />
          <Pagination page={query.data.page} totalPages={query.data.totalPages} totalElements={query.data.totalElements} onPage={(p) => list.update({ page: p })} />
        </>
      )}
    </>
  );
}
